import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tokenStore, type Tokens } from './tokenStore';
import { authApi } from '@/api/auth';
import { analytics } from '@/analytics';

interface AuthCtx {
  tokens: Tokens | null;
  session: { email: string | null; uuid: string | null } | null;
  isAuthenticated: boolean;
  signIn: (t: Tokens) => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<Tokens | null>(() => tokenStore.get());
  const qc = useQueryClient();

  useEffect(() => tokenStore.subscribe(setTokens), []);

  /**
   * Identity for analytics: the account's opaque uuid, never the email, RUC or razón social.
   * Runs on login and on a restored session, and before onboarding can mount — every funnel event
   * must already carry a user id, or GA4 counts a browser-then-app user as two visitors with a
   * 100% drop-off. Cleared on logout so the next account does not merge into this profile.
   */
  useEffect(() => {
    if (tokens?.uuid) {
      analytics.identify(tokens.uuid, tokens.isNewAccount == null ? undefined : { is_new_account: String(tokens.isNewAccount) });
    } else if (!tokens) {
      analytics.reset();
    }
  }, [tokens]);

  // When tokens vanish (401 → failed refresh), drop all cached server state.
  useEffect(() => {
    if (!tokens) qc.clear();
  }, [tokens, qc]);

  const signIn = useCallback((t: Tokens) => tokenStore.set(t), []);
  const signOut = useCallback(async () => {
    const t = tokenStore.get();
    try {
      if (t?.refreshToken) await authApi.logout(t.refreshToken);
    } catch {
      /* best effort */
    }
    tokenStore.clear();
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      tokens,
      session: tokens ? { email: tokens.email, uuid: tokens.uuid } : null,
      isAuthenticated: !!tokens?.accessToken,
      signIn,
      signOut,
    }),
    [tokens, signIn, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
